import { contrastRatio } from "./colorSystemPresentation.mjs";
import { paletteDifference, paletteHexAtLightness, paletteLab, validPaletteHex } from "./colorPaletteRefinement.mjs";

// Usage is a local preview decision, never a persisted color-role override.
export function analyzePalette(palette, usage = "dark-text", textSize = "normal") {
  const colors = palette?.colors || [];
  if (!colors.length || colors.some((color) => !validPaletteHex(color.hex)))
    return { conclusion: "Не удалось проверить цвета. Открой актуальную палитру.", recommendation: null, pairs: [], near: [] };
  const near = [];
  colors.forEach((a, i) => colors.slice(i + 1).forEach((b) => {
    const difference = paletteDifference(a.hex, b.hex);
    if (difference < 10) near.push({ a, b, difference });
  }));
  const surfaces = colors.filter((c) => ["primary_light", "neutral"].includes(c.role));
  const threshold = textSize === "large" ? 3 : 4.5;
  const text = colors.find((c) => c.role === (usage === "accent-text" ? "accent" : "primary_dark"));
  const pairs = usage === "surfaces" || !text ? [] : surfaces.map((background) => ({ text, background, ratio: contrastRatio(text.hex, background.hex), threshold }));
  let recommendation = null;
  const propose = (color, accepts, direction, reason) => {
    const originalL = paletteLab(color.hex)[0];
    // First passing half-L* step, at most six L*: one small, reproducible change.
    for (let step = 1; step <= 12; step += 1) {
      const hex = paletteHexAtLightness(color.hex, originalL + direction * step * 0.5);
      if (!hex || colors.some((c) => c.hex === hex) || !accepts(hex)) continue;
      recommendation = { stable_id: color.stable_id, old_hex: color.hex, hex, name: color.name, role: color.role, reason,
        expected_version_number: palette.version_number, expected_source_fingerprint: palette.source_fingerprint };
      break;
    }
  };
  let conclusion;
  if (usage === "surfaces") {
    const pair = near.find(({ a, b }) => surfaces.includes(a) && surfaces.includes(b));
    if (pair) {
      const target = pair.a.role === "neutral" ? pair.a : pair.b;
      const fixed = target === pair.a ? pair.b : pair.a;
      propose(target, (hex) => paletteDifference(hex, fixed.hex) >= 10,
        paletteLab(target.hex)[0] <= paletteLab(fixed.hex)[0] ? -1 : 1,
        `Если «${target.name}» и «${fixed.name}» должны разделять соседние фоны, небольшое изменение светлоты поможет различать их. Тон сохраняется.`);
      conclusion = `«${pair.a.name}» и «${pair.b.name}» близки. Для мягких фонов это допустимо; для отдельных соседних областей можно усилить различие.`;
    } else conclusion = "Светлые фоны различимы по этой проверке. Оснований менять оттенки не найдено.";
  } else {
    const failing = pairs.filter((pair) => pair.ratio < threshold);
    if (failing.length) {
      const limiting = failing.reduce((a, b) => a.ratio < b.ratio ? a : b);
      propose(text, (hex) => pairs.every((pair) => contrastRatio(hex, pair.background.hex) >= threshold),
        paletteLab(text.hex)[0] < paletteLab(limiting.background.hex)[0] ? -1 : 1,
        `Если «${text.name}» используется ${textSize === "large" ? "крупным" : "обычным"} текстом на светлых фонах, этот оттенок даст необходимый контраст. Для декоративного акцента замена не обязательна.`);
      conclusion = `«${text.name}» на «${limiting.background.name}»: контраст ${limiting.ratio.toFixed(2)}:1. Для ${textSize === "large" ? "крупного" : "обычного"} текста нужен ${threshold}:1. Это ограничение пары, а не всей палитры.`;
    } else if (pairs.length) conclusion = "Проверенные пары текста и фона читаемы. Для этого применения менять цвета не требуется.";
    else conclusion = "Для проверки текста нужны назначенные цвета текста и фона. Без этой пары вывод о читаемости не делаю.";
    if (near.some(({ a, b }) => surfaces.includes(a) && surfaces.includes(b)))
      conclusion += " Близкие светлые оттенки можно оставить для мягких фонов.";
  }
  return { conclusion, recommendation, pairs, near };
}
